import type { RunAIEditToolOutput } from './RunAIEditToolOutput';

// サーバーの maxSteps は client tool の往復ごとにリセットされ、再試行の歯止めにならない。
// 往復のたびにビューモデル全体を送り直すため、収束しない再試行はトークンを浪費し続ける。
export const MAX_CONSECUTIVE_INVALID_TOOL_INPUTS = 3;

const TRIPPED_MESSAGE =
  'この依頼での編集ツールの実行を打ち切りました。' +
  'ユーザーから新しい指示をもらうまで、編集ツールを呼び出さないでください。';

// 手動チャットとMCP経路は同じ runner を共有するが独立して並走する仕様なので、
// インスタンスは経路ごとに分けて持たせる。
export class AIEditToolFailureGuard {
  // 入力エラーと、一部しか適用できなかった呼び出し(partial/noop)をまとめて数える。
  // partial をリセット扱いにすると、消えたビューを含む同じ入力を送り続けられて歯止めが無くなる。
  // 数える単位は往復ではなくツール呼び出し。AIが1メッセージで編集ツールを複数回呼ぶと
  // 1往復で上限に達しうるが、失敗した入力を送り直させない点では同じなので許容する。
  private consecutiveInvalidInputs = 0;
  private tripped = false;

  get isTripped(): boolean {
    return this.tripped;
  }

  /** 新しいユーザー発話ごとに解除する。 */
  reset(): void {
    this.consecutiveInvalidInputs = 0;
    this.tripped = false;
  }

  async run(runner: () => Promise<RunAIEditToolOutput>): Promise<RunAIEditToolOutput> {
    if (this.tripped) {
      return { type: 'error', message: TRIPPED_MESSAGE };
    }

    // 編集ツールは失敗を例外でも返す。素通りさせると打ち切りが効かず、AIが同じ入力を送り直す往復が止まらない。
    let result: RunAIEditToolOutput;
    try {
      result = await runner();
    } catch (error) {
      result = {
        type: 'error',
        message: error instanceof Error ? error.message : '何らかの原因で処理の呼び出しに失敗しました',
        cause: 'invalidToolInput',
      };
    }

    if (result.type === 'ok') {
      this.consecutiveInvalidInputs = 0;
      return result;
    }

    // 権限不足やビューのロード中による失敗は、AIが入力を直しても解消しない。
    // 再試行しても同じ結果になるだけなので、入力エラーを待たず1回で打ち切る。
    if (result.type === 'error' && result.cause !== 'invalidToolInput') {
      this.tripped = true;
      return result;
    }

    this.consecutiveInvalidInputs += 1;
    if (this.consecutiveInvalidInputs >= MAX_CONSECUTIVE_INVALID_TOOL_INPUTS) {
      this.tripped = true;
      return { ...result, message: `${result.message}\n\n${TRIPPED_MESSAGE}` };
    }
    return result;
  }
}
